import { FC, useMemo } from "react";
import { Navigate } from "react-router-dom";

import { ROUTE_PATH } from "src/core/constants/routePath";
import { useTenant } from "src/core/contexts/TenantContext/TenantProvider";
import { tenantPaths } from "src/core/utils/route";

/**
 * RootRedirect :
 * Rôle fonctionnel :
 * - Gérer l'accès à la racine de l'application ("/").
 * - Choisir un tenant de départ valide pour l'utilisateur.
 * - Rediriger vers la route tenant-scoped correspondante.
 *
 * Règles de résolution du tenant :
 * 1) Si un tenant par défaut est présent dans le token ET autorisé,
 *    il est prioritaire.
 * 2) Sinon, on prend le premier tenant disponible.
 *
 * IMPORTANT :
 * - Aucun tenant n'est sélectionné ici s'il n'est pas validé par tenantService.
 * - Si aucun tenant ne peut être résolu, l'accès est interdit (403 globale).
 *
 * Ce composant ne dépend PAS de l'URL tenant.
 * Il est appelé uniquement sur la route "/".
 */
export const RootRedirect: FC = () => {
  const { availableTenants, defaultTenantIdFromToken } = useTenant();

  /**
   * Résout le tenant cible à partir :
   * - des tenants réellement accessibles
   * - du tenant par défaut issu du token (s'il est autorisé)
   *
   * Le calcul est mémoïsé pour éviter des recalculs inutiles.
   */
  const resolvedTenantId = useMemo(() => {
    if (availableTenants.length === 0) return null;

    const isDefaultAllowed = defaultTenantIdFromToken
      ? availableTenants.some((t) => t.id === defaultTenantIdFromToken)
      : false;

    if (isDefaultAllowed && defaultTenantIdFromToken) return defaultTenantIdFromToken;

    // Fallback : premier tenant accessible
    return availableTenants[0]?.id ?? null;
  }, [availableTenants, defaultTenantIdFromToken]);

  /**
   * Cas de sécurité :
   * - Aucun tenant n'a pu être résolu alors que l'utilisateur est authentifié.
   *
   * Ce cas ne devrait normalement pas arriver si :
   * - TenantsValidationGate est correctement positionné en amont.
   *
   * On garde néanmoins cette protection pour éviter un état incohérent.
   */
  if (!resolvedTenantId) {
    console.warn("[NAV] RootRedirect -> ERROR_403 (no resolved tenant)");
    return <Navigate to={ROUTE_PATH.ERROR_403} replace />;
  }

  /**
   * Redirection vers la racine tenant-scoped.
   */
  return <Navigate to={tenantPaths.home(resolvedTenantId)} replace />;
};
